import { Component, OnInit } from '@angular/core';
import {
  CustomerSubscriptionsService,
  CreateCheckoutSessionRequest,
  UpdateSubscriptionRequest,
} from 'ldt-billing-service-api';
import { AuthService } from 'src/app/auth/service/auth.service';
import { Log } from 'src/app/logger.service';
import { NotificationService } from 'src/app/shared/notification-service/notification.service';
import { environment } from 'src/environments/environment';
import { BillingService } from 'src/app/core/services/billing.service';
import { BaseProductSubscriptionComponent } from '../base-product-subscription.component';
import { Observable } from 'rxjs';

@Component({
    selector: 'app-billing-products-moneyball',
    templateUrl: './moneyball.component.html',
    styleUrls: ['./moneyball.component.scss'],
    standalone: false
})
export class MoneyballProductSubscriptionComponent
  extends BaseProductSubscriptionComponent
  implements OnInit
{
  // For now, mark everyone as beta unless they have a license
  isBeta: boolean = true;
  currentSubscriptionQty: number | null = null;
  selectedQty: number = 1;
  pricePerUser: number = 99;
  waitingForSubscriptionUpdate: boolean = false;
  isOrgAdmin$: Observable<boolean>;

  protected get productId(): string {
    return environment.moneyballSubscriptionProductId;
  }

  get totalCost(): number {
    return this.selectedQty * this.pricePerUser;
  }

  constructor(
    subscriptionsService: CustomerSubscriptionsService,
    authService: AuthService,
    notificationService: NotificationService,
    billingService: BillingService
  ) {
    super(subscriptionsService, authService, notificationService, billingService);
  }

  ngOnInit(): void {
    this.selectedQty = 1;
    super.initializeBaseSubscriptionLogic();
    this.isOrgAdmin$ = this.authService.$isOrgAdmin;
  }

  // Any time the subscription is loaded or changed, update the UI
  protected onSubscriptionLoaded(): void {
    Log.d(
      'MoneyballComponent: onSubscriptionLoaded, currentSubscription:',
      this.currentSubscription
    );
    if (this.currentSubscription && this.currentSubscription.product?.qty !== undefined) {
      this.isBeta = false;
      this.selectedQty = this.currentSubscription.product.qty || 1;
      this.currentSubscriptionQty = this.currentSubscription.product.qty || null;
    } else {
      this.selectedQty = 1;
      this.currentSubscriptionQty = null;
    }
    Log.d(
      'MoneyballComponent: onSubscriptionLoaded processed',
      this.currentSubscription,
      this.currentSubscriptionQty,
      this.selectedQty
    );
  }

  incrementUsers(): void {
    this.selectedQty++;
  }

  decrementUsers(): void {
    if (this.selectedQty > 1) {
      this.selectedQty--;
    }
  }

  allowChangeSubscription(): boolean {
    return (
      this.currentSubscriptionQty !== this.selectedQty &&
      !this.isPollingForUpdate &&
      !this.isLoadingSubscriptions
    );
  }

  // This is when changing the tier of the subscription
  updateSubscription(): void {
    if (!this.currentSubscription || !this.currentSubscription.id) {
      Log.e('MoneyballComponent: Current subscription not found for update.');
      this.notificationService.error('Cannot update: current subscription details are missing.');
      return;
    }
    if (this.selectedQty <= 0) {
      Log.e('MoneyballComponent: Invalid quantity for update:', this.selectedQty);
      this.notificationService.error('Cannot update: quantity must be at least 1.');
      return;
    }
    if (this.currentSubscription.product?.qty === this.selectedQty) {
      this.notificationService.info('This is already your current quantity.');
      return;
    }

    this.isLoadingSubscriptions = true;
    const req: UpdateSubscriptionRequest = {
      qty: this.selectedQty,
    };
    this.subscriptionsService
      .updateSubscription(this.authService.getSelectedOrgIdValue, this.currentSubscription.id, req)
      .subscribe({
        next: () => {
          this.pollForMoneyballUpdate(this.selectedQty);
        },
        error: (err) => super.handleBillingError(err, 'updating your subscription'),
      });
  }

  private pollForMoneyballUpdate(expectedQty: number): void {
    super.pollUntilConditionMet(
      () => this.currentSubscription?.product?.qty === expectedQty,
      'Subscription quantity successfully updated!'
    );
  }

  // This is for the initial subscription
  subscribe(): void {
    if (this.selectedQty <= 0) {
      Log.e('MoneyballComponent: Invalid quantity for new subscription:', this.selectedQty);
      this.notificationService.error('Cannot subscribe: quantity must be at least 1.');
      return;
    }

    const req: CreateCheckoutSessionRequest = {
      priceId: environment.moneyballSubscriptionPriceId,
      qty: this.selectedQty,
    };
    this.subscriptionsService
      .createCheckoutSession(this.authService.getSelectedOrgIdValue, req)
      .subscribe({
        next: (res) => super.handleStripeCheckoutSession(res.sessionUrl),
        error: (err) => super.handleBillingError(err, 'creating your subscription'),
      });
  }
}
